import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Notification } from '@usf/alert-types';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  map,
  Observable,
  Subscription,
  switchMap,
  take,
  zip,
} from 'rxjs';
import { ServiceHandlerService } from '../../../../../shared/services/service-handler.service';
import { PlatformService } from '@panamax/app-state';
import { ProductService } from '@shared/services/product/product.service';
import { OrderItem } from '@usf/ngrx-order';
import { PoDate, ProductSummary } from '@usf/product-types';
import { extractPoDates } from '../../../../../product-detail/utils/po-dates-util';
import { NotificationsService } from '@notifications/services/notifications.service';
import { ProductSubstitution } from '../product-substitution-card/product-substitution-card.component';
import { CutProductsSummary } from '../../models/warehouse-cuts-models';
import { CutProduct } from '../../models/warehouse-cuts-models';

interface OrderProductUnavailableViewModel {
  notification?: Notification;
  warehouseCutsOrderNbrs?: number[];
  cutProducts?: CutProduct[];
  cutProductsSummary?: CutProductsSummary;
  productSubstitutions: ProductSubstitution[];
  substitutedProducts?: number;
  unavailableProducts?: number;
  addedToCartProducts?: number;
}

// Add interface for the selection event
export interface CutProductSelectEvent {
  product: CutProduct;
  index: number;
}

@Component({
  selector: 'app-ordered-product-unavailable',
  templateUrl: './ordered-product-unavailable.component.html',
  styleUrls: ['./ordered-product-unavailable.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrderedProductUnavailableComponent implements OnInit, OnDestroy {
  @Output()
  public cutProductSelect: EventEmitter<CutProductSelectEvent> =
    new EventEmitter();

  @Input()
  get notification(): Notification {
    return this._notificationSubject.getValue();
  }

  set notification(value: Notification) {
    this._notificationSubject.next(value);
  }

  public orderProductUnavailableViewModel$: Observable<OrderProductUnavailableViewModel>;
  public orderedProductUnavailableItemCardViewModels$: Observable<CutProduct[]>;

  private _notificationSubscription: Subscription;
  private _notificationSubject = new BehaviorSubject<Notification>(null);

  private _orderProductUnavailableViewModelSubject: BehaviorSubject<OrderProductUnavailableViewModel> =
    new BehaviorSubject<OrderProductUnavailableViewModel>({
      productSubstitutions: [],
      cutProductsSummary: {
        selectedCount: 0,
        totalCases: 0,
        totalEaches: 0,
      },
      cutProducts: [],
    });

  constructor(
    public readonly platformService: PlatformService,
    private readonly _serviceHandler: ServiceHandlerService,
    private readonly productService: ProductService,
    private readonly notificationsService: NotificationsService,
  ) {
    this.orderProductUnavailableViewModel$ =
      this._orderProductUnavailableViewModelSubject.asObservable();
  }

  ngOnInit(): void {
    console.log(
      'OrderedProductUnavailableComponent initialized with notification:',
      this.notification,
    );

    // If notification has detail, process it
    this._notificationSubscription = this._notificationSubject
      .pipe(
        map(notification => {
          if (notification?.alertContext?.[0]?.value) {
            console.log(
              'Found notification details ID:',
              notification.alertContext[0].value,
            );
            this.getProducts(this.notification.alertContext[0].value)
              .then(viewModel => {
                console.log(
                  'Successfully loaded products viewModel:',
                  viewModel,
                );
                this._orderProductUnavailableViewModelSubject.next({
                  ...viewModel,
                  notification: this.notification,
                });
              })
              .catch(error => {
                console.error('Error building warehouse cuts data:', error);
                // Create a default view model for error case
                this._orderProductUnavailableViewModelSubject.next({
                  notification: this.notification,
                  warehouseCutsOrderNbrs: [],
                  unavailableProducts: 0,
                  substitutedProducts: 0,
                  addedToCartProducts: 0,
                  cutProducts: [],
                  cutProductsSummary: {
                    selectedCount: 0,
                    totalCases: 0,
                    totalEaches: 0,
                  },
                  productSubstitutions: [],
                });
              });
          } else {
            console.error(
              'Notification has no alertContext[0].value:',
              notification,
            );
          }
        }),
      )
      .subscribe();
  }

  public selectItem(cutProduct: CutProduct, index: number): void {
    // Update internal state
    this.updateSelectedProduct({ product: cutProduct, index });
    // Emit event to parent
    this.cutProductSelect.emit({ product: cutProduct, index });
  }

  public updateWarehouseDetails(
    detailsId: string,
    customerNumber: number,
    divisionNumber: number,
    products: any[],
  ) {
    return this.notificationsService.updateWarehouseDetails(
      detailsId,
      customerNumber,
      divisionNumber,
      products,
    );
  }

  private getProducts(
    detailsId: string,
  ): Promise<OrderProductUnavailableViewModel> {
    console.log('**** Getting products for detailsId:', detailsId);
    return new Promise(resolve => {
      this.notificationsService
        .getWarehouseDetails(detailsId)
        ?.pipe(
          take(1),
          // Extract initial data from warehouse details
          map(details => {
            // Initialize view model
            const viewModel: OrderProductUnavailableViewModel = {
              warehouseCutsOrderNbrs: details?.orderNumbers,
              unavailableProducts:
                details?.products?.filter(p => p.type === 'CUT' && !p.processed)
                  .length || 0,
              substitutedProducts:
                details?.products?.filter(p => p.type === 'SUBBED').length || 0,
              addedToCartProducts:
                details?.products?.filter(p => p.type === 'CUT' && p.processed)
                  .length || 0,
              cutProducts: [],
              cutProductsSummary: {
                selectedCount: 0,
                totalCases: 0,
                totalEaches: 0,
              },
              productSubstitutions: [],
            };

            // Extract product numbers by type
            const cutProductNbrs =
              details?.products
                ?.filter(p => p.type === 'CUT')
                .map(p => p.prodNbr) || [];
            const originalProductNbrs =
              details?.products
                ?.filter(p => p.type === 'ORIGINAL')
                .map(p => p.prodNbr) || [];
            const subbedProductNbrs =
              details?.products
                ?.filter(p => p.type === 'SUBBED')
                .map(p => p.prodNbr) || [];

            return {
              details,
              viewModel,
              cutProductNbrs,
              originalProductNbrs,
              subbedProductNbrs,
            };
          }),
          // Load all products
          switchMap(data => {
            const { cutProductNbrs, originalProductNbrs, subbedProductNbrs } =
              data;

            // Load all products and wait for completion
            return zip([
              this.productService.loadProducts$(cutProductNbrs),
              this.productService.loadProducts$(originalProductNbrs),
              this.productService.loadProducts$(subbedProductNbrs),
            ]).pipe(
              map(
                ([cutLoaded, originalLoaded, subbedLoaded]) =>
                  cutLoaded && originalLoaded && subbedLoaded,
              ),
              filter(loaded => loaded),
              map(() => data),
            );
          }),
          // Process products after they're loaded
          switchMap(data => {
            const {
              details,
              viewModel,
              cutProductNbrs,
              originalProductNbrs,
              subbedProductNbrs,
            } = data;

            return combineLatest([
              this.buildCutProducts(cutProductNbrs, details),
              this.productService
                .getProductsWithAlternatives([
                  ...originalProductNbrs,
                  ...subbedProductNbrs,
                ])
                .pipe(
                  filter(productsMap => {
                    // Ensure all original and substitution products are loaded
                    const allOriginalProductsPresent =
                      originalProductNbrs.every(
                        prodNbr =>
                          productsMap.has(prodNbr) &&
                          !!productsMap.get(prodNbr),
                      );
                    const allSubbedProductsPresent = subbedProductNbrs.every(
                      prodNbr =>
                        productsMap.has(prodNbr) && !!productsMap.get(prodNbr),
                    );

                    return (
                      allOriginalProductsPresent && allSubbedProductsPresent
                    );
                  }),
                ),
            ]).pipe(
              map(([cutProducts, productsMap]) => {
                return { cutProducts, productsMap, viewModel, details };
              }),
            );
          }),
          // Build the final view model
          map(({ cutProducts, productsMap, viewModel, details }) => {
            viewModel.cutProducts = cutProducts;
            viewModel.cutProductsSummary =
              this.calculateCutProductsSummary(cutProducts);
            viewModel.productSubstitutions = this.buildProductSubstitutions(
              details,
              productsMap,
            );

            return viewModel;
          }),
        )
        ?.subscribe({
          next: viewModel => {
            resolve(viewModel);
          },
          error: error => {
            console.error('Error building warehouse cuts data:', error);
            // Create a default view model for error case
            const defaultViewModel: OrderProductUnavailableViewModel = {
              warehouseCutsOrderNbrs: [],
              unavailableProducts: 0,
              substitutedProducts: 0,
              addedToCartProducts: 0,
              cutProducts: [],
              cutProductsSummary: {
                selectedCount: 0,
                totalCases: 0,
                totalEaches: 0,
              },
              productSubstitutions: [],
            };
            resolve(defaultViewModel);
          },
        });
    });
  }

  private calculateCutProductsSummary(
    cutProducts: CutProduct[],
  ): CutProductsSummary {
    return cutProducts.reduce(
      (acc, product) => {
        if (product.isSelected) {
          acc.selectedCount++;
          acc.totalCases +=
            (product.original?.orderItem.unitsReserved || 0) -
            (product.original?.orderItem.unitsOrdered.currentValue || 0) +
            ((product.substitute?.orderItem.unitsReserved || 0) -
              (product.substitute?.orderItem.unitsOrdered.currentValue || 0));
          acc.totalEaches +=
            (product.original?.orderItem.eachesReserved || 0) -
            (product.original?.orderItem.eachesOrdered.currentValue || 0) +
            ((product.substitute?.orderItem.eachesReserved || 0) -
              (product.substitute?.orderItem.eachesOrdered.currentValue || 0));
        }
        return acc;
      },
      { selectedCount: 0, totalCases: 0, totalEaches: 0 },
    );
  }

  private buildProductSubstitutions(
    details: any,
    productsMap: any,
  ): ProductSubstitution[] {
    return (
      details?.products
        ?.filter(subDetail => subDetail.type === 'SUBBED')
        .map(subDetail => {
          const originalDetail = details?.products.find(
            od =>
              od.type === 'ORIGINAL' && od.prodNbr === subDetail.substituteFor,
          );
          let originalProduct = productsMap.get(originalDetail?.prodNbr);
          let subProduct = productsMap.get(subDetail?.prodNbr);

          if (originalProduct?.summary) {
            originalProduct.summary.productNumber = originalDetail?.prodNbr;
          }
          if (subProduct?.summary) {
            subProduct.summary.productNumber = subDetail?.prodNbr;
          }

          return {
            original: originalDetail
              ? {
                  unitsOrdered: originalDetail.unitsOrdered,
                  eachesOrdered: originalDetail.eachesOrdered,
                  orderItem: this.createOrderItem(originalDetail),
                  product: originalProduct,
                }
              : null,
            substitute: {
              orderItem: this.createOrderItem(subDetail),
              product: subProduct,
            },
            isSelected: true,
          };
        }) || []
    );
  }

  private buildCutProducts(
    cutProductNbrs: any,
    details: any,
  ): Observable<CutProduct[]> {
    return this.productService
      .getProductsWithAlternatives(cutProductNbrs)
      ?.pipe(
        filter(productsMap => {
          const allProductsPresent = cutProductNbrs.every(
            prodNbr => productsMap.has(prodNbr) && !!productsMap.get(prodNbr),
          );
          if (!allProductsPresent) {
            console.log(
              'Not all products loaded yet, waiting for complete data',
            );
          }
          return allProductsPresent;
        }),
        map(productsMap => {
          const cutItems: CutProduct[] =
            details?.products
              ?.filter(p => p.type === 'CUT')
              .map((cutDetail): CutProduct => {
                let originalProduct = productsMap.get(cutDetail.prodNbr);
                if (originalProduct?.summary) {
                  originalProduct.summary.productNumber = cutDetail.prodNbr;
                }
                return {
                  original: {
                    orderItem: this.createOrderItem(cutDetail),
                    product: originalProduct,
                    processed: cutDetail.processed,
                    lastUpdatedBy: `${cutDetail.firstName} ${cutDetail.lastName} (${cutDetail.userName?.replace('[AD_AUTO]', '').trim()})`,
                    orderNumber: cutDetail.orderNumber,
                  },
                  substitute: null,
                  isSelected: cutDetail.processed ? false : true,
                };
              }) || [];
          return cutItems;
        }),
        map(cutItems => this.organizeUnavailableProducts(cutItems)),
      );
  }

  public updateSelectedProduct(event: CutProductSelectEvent): void {
    const currentViewModel =
      this._orderProductUnavailableViewModelSubject.getValue();

    // Get the product from the event
    const product = event.product;

    // Get the new selection state (opposite of current)
    const newSelectionState = !product.isSelected;

    // Update all products with matching productNumber
    const cutProducts = (currentViewModel?.cutProducts || []).map(p =>
      p.original.orderItem.productNumber ===
      product.original.orderItem.productNumber
        ? { ...p, isSelected: newSelectionState }
        : p,
    );

    // Calculate new summary
    const updatedSummary = this.calculateCutProductsSummary(cutProducts);

    // Update viewModel with new product selections
    this._orderProductUnavailableViewModelSubject.next({
      ...currentViewModel,
      cutProducts: cutProducts,
      cutProductsSummary: updatedSummary,
    });

    // Pass the event up to parent component
    this.cutProductSelect.emit(event);
  }

  private createOrderItem(detail: any): OrderItem {
    return {
      productNumber: detail?.prodNbr || '',
      unitsOrdered: {
        currentValue: detail?.unitsShipped || 0,
        previousValue: null,
        loading: false,
        error: null,
      },
      eachesOrdered: {
        currentValue: detail?.eachesShipped || 0,
        previousValue: null,
        loading: false,
        error: null,
      },
      eachPrice: detail?.eachPrice || 0,
      eachesReserved: detail?.eachesReserved || 0,
      substituteFor: detail?.substituteFor || 0,
      totalDollars: detail?.totalDollars || 0,
      unitPrice: detail?.unitPrice || 0,
      unitsReserved: detail?.unitsReserved || 0,
      updateUserId: '',
      updateDtm: '',
      addUserId: '',
      addDtm: '',
      sequence: 0,
      unitPriceOverride: 0,
      addOrderSource: '',
      updateOrderSource: '',
      addUserRole: '',
      updateUserRole: '',
      quantityAccepted: false,
      deleteFlag: false,
      substituteFlag: '',
      priceUOM: '',
      productExceptionCount: 0,
      purchasedFromVendor: 0,
      vendorLeadTime: 0,
      specialVendor: false,
    };
  }

  /**
   * Group and sort the CutProducts in the following order:
   * - in-stock
   * - expected today
   * - expected on a known date
   * - unavailable
   * @param cutItems - CutProducts
   * @returns the same items, grouped and sorted
   */
  private organizeUnavailableProducts(cutItems: CutProduct[]): CutProduct[] {
    const inStock: CutProduct[] = [];
    const unknownAvailability: CutProduct[] = [];
    // need a date to sort known
    const knownAvailability: [CutProduct, PoDate][] = [];
    cutItems.forEach((cutItem: CutProduct) => {
      if (cutItem.original?.product?.inventory?.isInStock) {
        inStock.push(cutItem);
        return;
      }
      const expectedInStock: PoDate = cutItem.original?.product?.summary
        ? this.extractEarliestPoDate(cutItem.original.product.summary)
        : undefined;
      if (expectedInStock && expectedInStock.scheduledArrivalDate) {
        // we have a date for these
        knownAvailability.push([cutItem, expectedInStock]);
      } else {
        unknownAvailability.push(cutItem);
      }
    });
    // sort the known by date
    knownAvailability.sort(this.compareProductAvailability);
    return [
      ...inStock,
      ...knownAvailability.map(([item]) => item), //extract cutItems
      ...unknownAvailability,
    ];
  }

  /**
   * uses utility method extractPoDates to determine which is available earlier
   * returns -1 if product1 < product2
   */
  private compareProductAvailability(
    product1: [CutProduct, PoDate],
    product2: [CutProduct, PoDate],
  ): number {
    let sorted = extractPoDates([product1[1], product2[1]]);
    return product1[1].formattedArrivalDate === sorted[0].formattedArrivalDate
      ? -1
      : 1;
  }

  private extractEarliestPoDate(
    productSummary: ProductSummary,
  ): PoDate | undefined {
    // sort poDates by scheduledArrivalDate
    const poDates = extractPoDates(productSummary?.poDates);
    // take the earliest
    return poDates?.length > 0 ? poDates[0] : undefined;
  }

  /**
   * TrackBy function for cut products to improve ngFor performance
   */
  public trackByCutProduct(index: number, item: CutProduct): number | string {
    return item.original?.orderItem?.productNumber || index;
  }

  /**
   * TrackBy function for product substitutions to improve ngFor performance
   */
  public trackBySubstitution(
    index: number,
    item: ProductSubstitution,
  ): number | string {
    return (
      item.original?.product?.productNumber ||
      item.substitute?.product?.productNumber + '-sub' ||
      index
    );
  }

  ngOnDestroy(): void {
    if (this._notificationSubscription) {
      this._notificationSubscription.unsubscribe();
    }
  }
}
